import { useEffect, useRef, useState } from "react";
import Header from "../header/Header";
import Sidebar from "../Sidebar/Sidebar";
import {
  Avatar,
  Button,
  Col,
  message,
  Modal,
  Row,
  Table,
  Input,
  Select,
  Popover,
  Tooltip,
} from "antd";
import {
  SearchOutlined,
  FilterOutlined,
  UserOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { getAllRecords } from "../../services/ApiService";
import { Spin } from "antd";
import "react-datepicker/dist/react-datepicker.css";
import "../MasterDatabase/MasterDB.css";
import axios from "axios";
import * as XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";

const AllRecords = () => {
  const [allRequest, setAllRequest] = useState([]);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 100,
  });
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [countryLoader, setCountryLoader] = useState(false);
  const [filterPopoverVisible, setFilterPopoverVisible] = useState(false);
  const [searchFilters, setSearchFilters] = useState({
    client_name: "",
    // client_customer_name: "",
    employee_name: "",
  });

  const generateColumnsFromData = (Columnkeys) => {
    const keys = Columnkeys.filter(
      (item) =>
        item !== "client_id" &&
        item !== "client_name" &&
        item !== "id" &&
        item !== "status" &&
        item !== "full_name"
    );

    const dynamicColumns = keys.map((key) => ({
      title: key
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase()),
      dataIndex: key,
      key: key,
      ellipsis: true,
      render: (text) => (
        <div style={{ minWidth: 100, whiteSpace: "nowrap" }}>
          {text && text !== "null" ? text : ""}
        </div>
      ),
    }));

    const fullNameColumn = {
      title: "Employee Name",
      dataIndex: "full_name",
      key: "full_name",
      ellipsis: true,
      render: (text) => (
        <div style={{ minWidth: 100, whiteSpace: "nowrap" }}>
          {text && text !== "null" ? text : ""}
        </div>
      ),
    };

    const clientNameColumn = {
      title: "Client Name",
      fixed: "left",
      key: "client_name",
      render: (_, record) => (
        <div
          style={{
            display: "flex",
            gap: "0.1rem",
            alignItems: "center",
          }}
        >
          <Avatar size={20} icon={<UserOutlined />} />
          <span style={{ fontSize: "0.8rem", padding: "0.5rem 1rem" }}>
            {record["client_name"]}
          </span>
        </div>
      ),
      responsive: ["xs", "sm", "sm"],
    };

    return [clientNameColumn, fullNameColumn, ...dynamicColumns];
  };

  const fetchData = async (
    page = 1,
    pageSize = 100,
    filters = {},
    download
  ) => {
    setLoading(true);
    try {
      let requestRes = await getAllRecords(page, pageSize, filters, download);
      if (typeof requestRes === "string") {
        requestRes = requestRes.replace(/\bNaN\b/g, "null");
        requestRes = JSON.parse(requestRes);
      }
      if (requestRes?.responseCode === 200) {
        setTotal(requestRes?.total_records || 0);
        const Response = requestRes?.data;
        setAllRequest(Response);
        if (Response.length > 0) {
          const dynamicCols = generateColumnsFromData(requestRes?.all_columns);
          setColumns(dynamicCols);
        } else {
          setColumns([]);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    const clearedFilters = {
      client_name: "",
      // client_customer_name: "",
      employee_name: "",
    };
    setSearchFilters(clearedFilters);
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchData(1, pagination.pageSize, clearedFilters, false);
  };

  const handleTableChange = (paginationInfo) => {
    const newPagination = {
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize,
    };
    setPagination(newPagination);
    fetchData(
      paginationInfo.current,
      paginationInfo.pageSize,
      searchFilters,
      false
    );
  };

  useEffect(() => {
    fetchData(1, 50, {}, false);
    fetchCountry();
  }, []);

  const fetchCountry = async () => {
    try {
      setCountryLoader(true);
      
      const apiKey = process.env.REACT_APP_COUNTRY_API_KEY || "rc_live_5626b228dd014fd3b0d88192c5138820";

      const res = await axios.get("https://api.restcountries.com/countries/v5", {
        withCredentials: false,
        headers: {
          "Authorization": `Bearer ${apiKey}`
        }
      });

      if (res.status === 200) {
        // 1. Access the deep array layout safely
        const rawObjects = res.data?.data?.objects || [];

        // 2. Sort and map using the correct nested properties
        const CountryList = rawObjects
          .sort((a, b) => {
            const nameA = a.names?.common || "";
            const nameB = b.names?.common || "";
            return nameA.localeCompare(nameB);
          })
          .map((item) => ({
            // Extracts "Abkhazia" from item.names.common
            label: item.names?.common || "Unknown Country",
            value: item.names?.common || "Unknown Country",
          }));

        setCountries(CountryList);
      } else {
        message.error("Error in fetching Country Data");
      }
    } catch (error) {
      console.error("Fetch error details:", error);
      message.error("Error in fetching Country Data");
    } finally {
      setCountryLoader(false);
    }
  };

  const handleDownload = async () => {
    setLoading(true);
    try {
      let requestRes = await getAllRecords(1, 5000, searchFilters, true);
      if (typeof requestRes === "string") {
        requestRes = requestRes.replace(/\bNaN\b/g, "null");
        requestRes = JSON.parse(requestRes);
      }

      if (requestRes?.responseCode === 200) {
        const Response = requestRes?.data;

        if (Array.isArray(Response)) {
          const cleanData = Response.map((row) =>
            Object.fromEntries(
              Object.entries(row).map(([key, value]) => [key, value ?? ""])
            )
          );

          const headers = requestRes?.all_columns;

          const worksheet = XLSX.utils.json_to_sheet(cleanData, {
            header: headers,
          });

          const columnWidths = headers.map((header) => {
            const maxContentLength = Math.max(
              header.length,
              ...cleanData.map((row) =>
                row[header] ? String(row[header]).length : 0
              )
            );
            return { wch: maxContentLength + 2 };
          });
          worksheet["!cols"] = columnWidths;

          headers.forEach((header, colIndex) => {
            const cellRef = XLSX.utils.encode_cell({ r: 0, c: colIndex });
            worksheet[cellRef] = worksheet[cellRef] || { t: "s", v: header };
            worksheet[cellRef].s = {
              font: {
                bold: true,
                color: { rgb: "FFFFFF" },
              },
              fill: {
                fgColor: { rgb: "4F81BD" },
              },
              alignment: {
                horizontal: "center",
                vertical: "center",
              },
            };
          });

          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, worksheet, "All_Records");

          const excelBuffer = XLSX.write(workbook, {
            bookType: "xlsx",
            type: "array",
          });

          const fileData = new Blob([excelBuffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });

          saveAs(fileData, "All_Records.xlsx");
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
  <>
    <div className="main-box">
      <Sidebar activeId={9} />
      <main className="main-content">
        <Spin
          spinning={loading || countryLoader}
          size="large"
          tip="Loading..."
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              height: "100vh",
            }}
          >
            <Header title="All Records" />
            
            {/* Main content container */}
            <div style={{ flex: 1, padding: "1rem", overflowY: "auto", display: "flex", flexDirection: "column" }}>
              
              {/* Row 1: Filters in a visible single row + Clear button */}
              <div
                style={{
                  display: "flex",
                  gap: "0.75rem",
                  alignItems: "center",
                  marginBottom: "0.75rem",
                  flexWrap: "wrap",
                }}
              >
                <Input
                  placeholder="Client Name"
                  prefix={<SearchOutlined />}
                  value={searchFilters.client_name}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchFilters((prev) => {
                      const updated = { ...prev, client_name: value };
                      setPagination((p) => ({ ...p, current: 1 }));
                      fetchData(1, pagination.pageSize, updated, false);
                      return updated;
                    });
                  }}
                  style={{ fontSize: "0.8rem", width: "220px" }}
                  allowClear
                />

                <Input
                  placeholder="Employee Name"
                  prefix={<SearchOutlined />}
                  value={searchFilters.employee_name}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchFilters((prev) => {
                      const updated = { ...prev, employee_name: value };
                      setPagination((p) => ({ ...p, current: 1 }));
                      fetchData(1, pagination.pageSize, updated, false);
                      return updated;
                    });
                  }}
                  style={{ fontSize: "0.8rem", width: "220px" }}
                  allowClear
                />

                <Button
                  onClick={() => {
                    clearFilters();
                  }}
                  style={{ color: "red", borderColor: "#ff4d4f" }}
                >
                  Clear
                </Button>

                <Tooltip title="Download Excel">
                  <Button
                    shape="circle"
                    style={{
                      backgroundColor: "#097249",
                      color: "white",
                    }}
                    onClick={handleDownload}
                    icon={<DownloadOutlined />}
                  />
                </Tooltip>
              </div>

              {/* Table Body with fixed scroll area */}
              <Table
                columns={columns}
                dataSource={allRequest}
                rowKey="id"
                pagination={{
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  total: total,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50", "100"],
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} of ${total} records`,
                }}
                onChange={handleTableChange}
                scroll={{ x: "max-content", y: 350 }}
                style={{
                  fontSize: "0.7rem",
                  borderRadius: "1.5rem",
                  background: "#FFF",
                  flex: 1,
                }}
                className="masterTb custom-scrollbar"
              />
            </div>
          </div>
        </Spin>
      </main>
    </div>
  </>
  );
};

export default AllRecords;
